import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../api/axiosInstance";

const TABS = ["state", "city", "zip"];
const SORT_OPTIONS = [
  { value: "total_leads", label: "Total leads" },
  { value: "contact_saved_count", label: "Contacts saved" },
  { value: "other_status_count", label: "Other status" },
  { value: "label", label: "Label A–Z" },
];

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="19" r="1" fill="currentColor" />
  </svg>
);

const ExpandIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const NoDataFound = () => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
    <span className="text-gray-400 text-sm font-medium">No Data Found</span>
  </div>
);

const LoadingRows = ({ cols = 4, rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-sky-50"}>
        {Array.from({ length: cols }).map((_, j) => (
          <td key={j} className="px-3 py-2 border-b border-sky-100">
            <div className="h-3 rounded bg-gray-200 animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const LeadsStat = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ state: [], city: [], zip: [] });
  const [tab, setTab] = useState("state");
  const [sortBy, setSortBy] = useState("total_leads");
  const [sortDir, setSortDir] = useState("desc");
  const [query, setQuery] = useState("");

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("api/dealmachine_properties/stats");
      if (response.status === 200) {
        setData({
          state: response.data.data.state || [],
          city: response.data.data.city || [],
          zip: response.data.data.zip || [],
        });
      }
    } catch (error) {
      console.error("Error fetching leads.", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []); // eslint-disable-line

  const filteredRows = useMemo(() => {
    let list = data[tab] || [];

    if (query) {
      const q = query.toLowerCase();
      list = list.filter((r) => r.label.toLowerCase().includes(q));
    }

    return list;
  }, [data, tab, query]);

  const rows = useMemo(() => {
    const list = [...filteredRows];

    list.sort((a, b) => {
      if (sortBy === "label") {
        return sortDir === "asc" ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
      }

      return sortDir === "asc" ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
    });

    return list;
  }, [filteredRows, sortBy, sortDir]);

  const totals = useMemo(
    () => ({
      leads: rows.reduce((s, r) => s + r.total_leads, 0),
      saved: rows.reduce((s, r) => s + r.contact_saved_count, 0),
      other: rows.reduce((s, r) => s + r.other_status_count, 0),
    }),
    [rows],
  );

  const maxLeads = useMemo(() => Math.max(...rows.map((r) => r.total_leads), 1), [rows]);

  const handleTabChange = (t) => {
    setLoading(true);
    setTab(t);
    setQuery("");
    setTimeout(() => setLoading(false), 0);
  };

  const handleColSort = (col) => {
    if (col === sortBy) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir(col === "label" ? "asc" : "desc");
    }
  };

  const tabTitle = { state: "States", city: "Cities", zip: "Zip Codes" };

  return (
    <div className="p-5 max-w-7xl mx-auto min-h-[90vh]">
      {/* Toolbar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 items-center mb-5">
        <div className="grid grid-cols-3 border border-sky-400 rounded-lg overflow-hidden text-nowrap">
          {TABS.map((t) => (
            <button key={t} onClick={() => handleTabChange(t)} className={`px-4 py-1.5 text-sm font-medium transition-colors capitalize ${tab === t ? "bg-sky-500 text-white" : "text-sky-700 hover:bg-sky-50"}`}>
              {t === "zip" ? "Zip codes" : t === "city" ? "Cities" : `${t}s`}
            </button>
          ))}
        </div>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="flex-1 min-w-40 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <div className="grid grid-cols-1 sm:grid-cols-3 sm:col-span-2 md:col-span-1 gap-2 items-center">
          <span className="text-xs text-gray-500 w-full text-center">Sort by</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full text-center text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg">
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select value={sortDir} onChange={(e) => setSortDir(e.target.value)} className="w-full text-center text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg">
            <option value="desc">High → Low</option>
            <option value="asc">Low → High</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
        {[
          { label: "Total leads", val: totals.leads.toLocaleString(), sub: `${rows.length} locations` },
          {
            label: "Contacts saved",
            val: totals.saved.toLocaleString(),
            sub: `${totals.leads ? Math.round((totals.saved / totals.leads) * 100) : 0}% save rate`,
          },
          {
            label: "Other status",
            val: totals.other.toLocaleString(),
            sub: `${totals.leads ? Math.round((totals.other / totals.leads) * 100) : 0}% of total`,
          },
        ].map((c) => (
          <div key={c.label} className="bg-sky-50 border border-sky-200 rounded-lg p-3">
            <div className="text-xs text-sky-700 uppercase tracking-wide mb-1">{c.label}</div>
            <div className="text-2xl font-medium text-sky-900">{c.val}</div>
            <div className="text-xs text-sky-600 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Leads Table — same card style as GHL calls table */}
      <div className="rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <span className="text-xs text-gray-500 font-medium">Leads by {tabTitle[tab]}</span>
          <button className="text-gray-400 hover:text-gray-600">
            <MoreIcon />
          </button>
        </div>

        <div className="w-full h-full overflow-hidden">
          {loading ? (
            <>
              {/* Loading state: show skeleton rows inside the table */}
              <div className="w-full overflow-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      {["Location", "Total leads", "Contacts saved", "Other status"].map((label) => (
                        <th key={label} className="bg-sky-400 text-white px-3 py-2 text-left font-medium text-xs tracking-wide">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <LoadingRows cols={4} rows={6} />
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-2">
                <ExpandIcon />
              </div>
            </>
          ) : rows.length === 0 ? (
            <>
              <NoDataFound />
              <div className="flex justify-end">
                <ExpandIcon />
              </div>
            </>
          ) : (
            <div className="w-full overflow-auto relative">
              <table className="w-full text-xs m-0 border-collapse">
                <thead className="sticky top-0 z-10 bg-sky-400 text-white shadow-sm shadow-sky-100">
                  <tr>
                    {[
                      { col: "label", label: "Location", w: "36%" },
                      { col: "total_leads", label: "Total leads", w: "18%" },
                      { col: "contact_saved_count", label: "Contacts saved", w: "23%" },
                      { col: "other_status_count", label: "Other status", w: "23%" },
                    ].map(({ col, label, w }) => (
                      <th key={col} style={{ width: w }} onClick={() => handleColSort(col)} className="border-none p-2 text-left cursor-pointer hover:bg-sky-500 transition-colors">
                        {label} {sortBy === col ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const pct = Math.round((r.total_leads / maxLeads) * 100);
                    const savePct = r.total_leads ? Math.round((r.contact_saved_count / r.total_leads) * 100) : 0;
                    const otherPct = r.total_leads ? Math.round((r.other_status_count / r.total_leads) * 100) : 0;
                    return (
                      <tr key={r.label} className={`border-t text-[10px] ${i % 2 === 0 ? "bg-sky-300/0" : "bg-sky-300/10"} hover:bg-sky-300/50 transition-colors`}>
                        <td className="border-none p-2 font-medium text-nowrap">
                          {r.label}
                          <div className="h-1 mt-1 rounded bg-sky-200 overflow-hidden">
                            <div className="h-full bg-sky-400 rounded" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                        <td className="border-none p-2">{r.total_leads.toLocaleString()}</td>
                        <td className="border-none p-2 text-nowrap">
                          <span className="bg-sky-100 text-sky-800 font-medium px-2 py-0.5 rounded">{r.contact_saved_count.toLocaleString()}</span>
                          <span className="text-sky-600 ml-1">{savePct}%</span>
                        </td>
                        <td className="border-none p-2 text-nowrap">
                          <span className="bg-amber-100 text-amber-800 font-medium px-2 py-0.5 rounded">{r.other_status_count.toLocaleString()}</span>
                          <span className="text-amber-700 ml-1">{otherPct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsStat;
